import { useMutation } from '@tanstack/react-query';
import { useState, useCallback, useMemo } from 'react';
import { Next, FormField, FormElementGroup, Input, Button, ButtonVariant, FieldMessageType } from '@shipengine/giger';
import { useSdk } from '@packlink/packlink-sdk-provider';
import { useTranslation } from '@packlink/translation-provider';
import { getTitleStyles, getVoucherToggleStyles, getFormControlsContainerStyles } from './PaymentVoucherStyles.js';
import { jsxs, jsx } from '@emotion/react/jsx-runtime';

let IErrorVoucherTranslation = /*#__PURE__*/function (IErrorVoucherTranslation) {
  IErrorVoucherTranslation["VOUCHERNOTFOUND"] = "form.voucher.error-not-found";
  IErrorVoucherTranslation["VOUCHERALREADYUSED"] = "form.voucher.error-already-used";
  IErrorVoucherTranslation["VOUCHERINVALID"] = "form.voucher.error-invalid";
  IErrorVoucherTranslation["VOUCHERGENERICERROR"] = "form.voucher.error-generic";
  return IErrorVoucherTranslation;
}({});
const PaymentVoucher = _ref => {
  let {
    applyVoucher,
    onRemoveVoucher,
    serviceId,
    clientId,
    shipments,
    showHeader = true,
    ...rest
  } = _ref;
  const {
    t
  } = useTranslation();
  const sdk = useSdk();
  const [voucher, setVoucher] = useState();
  const [voucherName, setVoucherName] = useState('');
  const [isOpened, setIsOpened] = useState(false);
  const handleChangeVoucher = useCallback(_ref2 => {
    let {
      target: {
        value
      }
    } = _ref2;
    setVoucherName(value.toUpperCase());
  }, []);
  const toggleVoucher = e => {
    e.preventDefault();
    setIsOpened(!isOpened);
  };
  const handleSuccess = response => {
    setVoucher(Array.isArray(response) ? response[0] : response);
    applyVoucher(response);
  };
  const validateMutation = useMutation({
    mutationFn: _ref3 => {
      let {
        voucherName,
        serviceId,
        client_id
      } = _ref3;
      return sdk.v1.vouchers.validate(voucherName, {
        serviceId,
        client_id
      });
    },
    onSuccess: handleSuccess
  });
  const validateBulkMutation = useMutation({
    mutationFn: _ref4 => {
      let {
        voucherName,
        shipments
      } = _ref4;
      return sdk.v1.vouchers.validateShipments(voucherName, shipments);
    },
    onSuccess: handleSuccess
  });
  const errorResponse = validateMutation.error || validateBulkMutation.error;
  const isLoading = validateMutation.isPending || validateBulkMutation.isPending;
  const error = useMemo(() => {
    var _errorResponse$code;
    if (!errorResponse) return null;
    const errorCode = errorResponse === null || errorResponse === void 0 ? void 0 : (_errorResponse$code = errorResponse.code) === null || _errorResponse$code === void 0 ? void 0 : _errorResponse$code.toUpperCase();
    if (errorCode && errorCode in IErrorVoucherTranslation) {
      return t(IErrorVoucherTranslation[errorCode]);
    } else {
      return t(IErrorVoucherTranslation.VOUCHERGENERICERROR);
    }
  }, [errorResponse, t]);
  const addVoucher = () => {
    if (voucherName) {
      if (shipments && shipments.length > 1) {
        validateBulkMutation.mutate({
          voucherName,
          shipments
        });
      } else if (serviceId || shipments && shipments.length === 1) {
        validateMutation.mutate({
          voucherName,
          serviceId: serviceId || (shipments === null || shipments === void 0 ? void 0 : shipments[0].serviceId),
          client_id: clientId
        });
      }
    }
  };
  const removeVoucher = () => {
    onRemoveVoucher();
    setVoucher(undefined);
    setVoucherName('');
  };
  const getFieldMessage = () => {
    if (error) {
      return {
        type: FieldMessageType.ERROR,
        content: error
      };
    } else {
      return;
    }
  };
  return jsxs("div", {
    ...rest,
    children: [showHeader && jsx("h3", {
      css: getTitleStyles,
      children: t('form.voucher.title')
    }), jsx(Next.Link, {
      role: "link",
      css: getVoucherToggleStyles,
      onClick: toggleVoucher,
      children: t('form.voucher.title')
    }), jsx("div", {
      css: getFormControlsContainerStyles(isOpened),
      children: jsx(FormField, {
        message: getFieldMessage(),
        children: jsxs(FormElementGroup, {
          children: [jsx(Input, {
            type: "text",
            name: "voucher",
            label: t('form.voucher.label'),
            onChange: handleChangeVoucher,
            disabled: !!voucher,
            value: voucherName,
            isInvalid: !!error
          }), jsx(Button, {
            disabled: !voucherName,
            type: "button",
            variant: voucher ? ButtonVariant.OUTLINED : ButtonVariant.FILLED,
            onClick: voucher ? removeVoucher : addVoucher,
            isLoading: isLoading,
            children: voucher ? t('form.voucher.remove') : t('form.voucher.add')
          })]
        })
      })
    })]
  });
};

export { IErrorVoucherTranslation, PaymentVoucher };
//# sourceMappingURL=PaymentVoucher.js.map
